import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Upload, RefreshCw, ExternalLink, Pencil, Download, FileText } from 'lucide-react';
import {
  getPaymentPostings, getPaymentSummary, getBookingById, getAllBookingBalances,
  type PaymentPosting, type PaymentSummary, type PaymentCategory, type ImportedBooking, type BookingBalance,
} from '../api';
import MatchPostingModal from '../components/MatchPostingModal';
import BookingDetailModal from '../components/BookingDetailModal';
import { Button } from '../components/ui';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { downloadCsv, csvNumber, type CsvColumn } from '../utils/csv';
import Pagination from '../components/Pagination';

const CATEGORY_LABEL: Record<PaymentCategory, string> = {
  rental: 'Udlejning',
  extra: 'Ekstra betaling',
  credit: 'Modtagne penge',
  cancellation: 'Aflysningsgebyr',
  refusion: 'Refusion',
  subscription: 'Abonnement',
  boost: 'Boost',
  bank: 'Bank-udbetaling',
  owed: 'Skyldigt beløb',
  other: 'Andet',
};

// Match-status i klartekst til eksporten. På skærmen bæres den af farve og placering;
// i et regneark skal den kunne læses og filtreres på som tekst.
const MATCH_LABEL: Record<string, string> = {
  auto:      'Auto-matchet',
  manual:    'Manuelt matchet',
  ignored:   'Ignoreret',
  unmatched: 'Ikke matchet',
};

const CATEGORY_COLOR: Record<PaymentCategory, string> = {
  rental: 'bg-sage-100 text-sage-700',
  extra: 'bg-cream-200 text-ink-700',
  credit: 'bg-sage-50 text-sage-700',
  cancellation: 'bg-rose-100 text-rose-700',
  refusion: 'bg-accent-100 text-accent-500',
  subscription: 'bg-ink-100 text-ink-700',
  boost: 'bg-ink-100 text-ink-700',
  bank: 'bg-ink-50 text-ink-500',
  owed: 'bg-rose-100 text-rose-700',
  other: 'bg-ink-50 text-ink-500',
};

// Bank-posteringer dækker både udbetalinger og udbetalingsgebyrer (+50 kr).
// Vi adskiller dem her ud fra beløbet så badge bliver mere sigende.
function getDisplay(category: PaymentCategory, amount: number): { label: string; colorClass: string } {
  if (category === 'bank') {
    if (Math.abs(amount) <= 100) return { label: 'Udbetalingsgebyr', colorClass: 'bg-ink-100 text-ink-700' };
    return { label: 'Udbetaling til bank', colorClass: 'bg-ink-50 text-ink-500' };
  }
  return { label: CATEGORY_LABEL[category], colorClass: CATEGORY_COLOR[category] };
}

const fmtAmount = (n: number) => n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Returnér undertekst der KUN viser detalje udover badge-label.
// "Modtagne penge (Manglende brændstof)" + badge "Modtagne penge" → "Manglende brændstof"
// "Refusion for brændstof" + badge "Refusion" → "for brændstof"
// "Udlejning" + badge "Udlejning" → "" (skjul)
function describeBeyondLabel(description: string, label: string): string {
  const desc = description.trim();
  const lab = label.trim();
  if (!desc || !lab) return desc;
  if (desc.toLowerCase() === lab.toLowerCase()) return '';
  // Fjern label fra start eller slut
  const lower = desc.toLowerCase();
  let stripped = desc;
  if (lower.startsWith(lab.toLowerCase())) {
    stripped = desc.slice(lab.length);
  } else if (lower.endsWith(lab.toLowerCase())) {
    stripped = desc.slice(0, desc.length - lab.length);
  }
  // Trim whitespace og fjern omgivende parenteser/dashes
  return stripped.replace(/^[\s\-–—]+|[\s\-–—]+$/g, '').replace(/^\(|\)$/g, '').trim();
}
const MONTHS_DK = ['januar','februar','marts','april','maj','juni','juli','august','september','oktober','november','december'];
const fmtDate = (iso: string) => {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${parseInt(m[3])}. ${MONTHS_DK[parseInt(m[2])-1].slice(0,3)} ${m[1]}`;
};

export default function GoMoreAccountPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [postings, setPostings] = useState<PaymentPosting[]>([]);
  const [summary, setSummary]   = useState<PaymentSummary | null>(null);
  const [loading, setLoading]   = useState(true);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo,   setFilterTo]   = useState('');
  const [filterCategory, setFilterCategory] = useState<PaymentCategory | ''>('');
  // 'unmatched' = mangler en booking. 'mismatch' = derudover posteringer på bookinger hvor det
  // modtagne ikke stemmer med det forventede — dér hvor der skal kigges ved afstemning.
  const [show, setShow] = useState<'all' | 'unmatched' | 'mismatch'>('all');
  const [balances, setBalances] = useState<Record<number, BookingBalance>>({});
  const [page, setPage] = useState(1);
  const [editPosting, setEditPosting] = useState<PaymentPosting | null>(null);
  const [viewBooking, setViewBooking] = useState<ImportedBooking | null>(null);

  const openBooking = async (bookingId: number) => {
    try {
      const b = await getBookingById(bookingId);
      setViewBooking(b);
    } catch { /* ignore */ }
  };

  const reload = async () => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (filterFrom) params.from = filterFrom;
    if (filterTo)   params.to   = filterTo;
    if (filterCategory) params.category = filterCategory;
    const [p, s, b] = await Promise.all([
      getPaymentPostings(params),
      getPaymentSummary(filterFrom || filterTo ? { from: filterFrom || undefined, to: filterTo || undefined } : {}),
      getAllBookingBalances(),
    ]);
    setPostings(p);
    setSummary(s);
    setBalances(b);
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterFrom, filterTo, filterCategory]);

  // Skift af filter skal starte forfra. Uden det kan man stå på side 7 af et udvalg der
  // pludselig kun har 2 sider, og se en tom tabel uden at forstå hvorfor.
  useEffect(() => { setPage(1); }, [filterFrom, filterTo, filterCategory, show]);

  // Aktuel saldo = den nyeste postering (første række, da backend returnerer DESC)
  const aktuelSaldo = useMemo(() => {
    if (postings.length === 0) return 0;
    return postings[0].saldo;
  }, [postings]);

  // Visningsfilter. Ignorerede tæller ikke som "stemmer ikke": dem har backend eller brugeren
  // bevidst lagt til side (udbetalinger, abonnement).
  const visiblePostings = useMemo(() => {
    if (show === 'all') return postings;
    return postings.filter(p => {
      if (p.match_type === 'unmatched') return true;
      if (show === 'unmatched' || !p.booking_id) return false;
      const bal = balances[p.booking_id];
      // Afventer afregning er ikke en afvigelse — kun det ingen ubetalt post forklarer
      return !!bal && (bal.status === 'missing' || bal.status === 'overflow');
    });
  }, [postings, show, balances]);

  // Alle posteringer hentes fortsat i ét kald. Pagineringen er ren visning, og det ER et krav
  // her: den løbende saldo regnes af backend hen over HELE sættet, så en side-opdelt hentning
  // ville give hver side sin egen forkerte startsaldo. Kategori-summerne og saldo-kortet
  // dækker derfor også hele udvalget og ikke kun den viste side.
  const pageSize = settings.itemsPerPage;
  const totalPages = Math.max(1, Math.ceil(visiblePostings.length / pageSize));
  const shownPage = Math.min(page, totalPages);  // klemt fast, så en sletning ikke efterlader en tom side
  const pagedPostings = visiblePostings.slice((shownPage - 1) * pageSize, shownPage * pageSize);

  // Eksport til afstemning. Tager HELE det filtrerede udvalg — ikke kun den viste side.
  // Periode-, kategori- og "kun ikke-matchede"-filtrene gælder, så man slipper for at vælge
  // dem igen, men pagineringen er en visningsting og må ikke skære data af eksporten.
  //
  // Beskrivelsen kommer med RÅ ved siden af den pæne type-etiket: badge'en er kortet ned til det
  // læsbare, men ved afstemning er det den fulde tekst fra GoMore man sammenligner mod.
  // Booking-UID'et er den vigtigste kolonne — det er dét der kobler en betaling til en udlejning.
  // Datoen skrives som ISO (YYYY-MM-DD): den sorterer korrekt både som tekst og som dato, og er
  // uafhængig af regnearkets landeindstilling.
  const exportCsv = () => {
    const cols: CsvColumn<PaymentPosting>[] = [
      { header: 'Dato',        value: p => p.posting_date },
      { header: 'Type',        value: p => getDisplay(p.category, Number(p.amount)).label },
      { header: 'Beskrivelse', value: p => p.description },
      { header: 'Lejer',       value: p => p.renter_name },
      { header: 'Bil',         value: p => p.registration_number },
      { header: 'Beløb',       value: p => csvNumber(p.amount, settings.numberFormat) },
      { header: 'Saldo',       value: p => csvNumber(p.saldo, settings.numberFormat) },
      { header: 'Match',       value: p => MATCH_LABEL[p.match_type] ?? p.match_type },
      { header: 'Sikkerhed',   value: p => p.match_confidence ?? '' },
      { header: 'Booking',     value: p => p.booking_uid ?? '' },
      { header: 'Booking-ID',  value: p => p.booking_id ?? '' },
      { header: 'Note',        value: p => p.notes ?? '' },
    ];
    downloadCsv('gomore-konto', cols, visiblePostings, settings.numberFormat);
  };

  // Periodetekst til PDF-hovedet. Datofiltrene er frie, så perioden kan være alt fra en enkelt
  // dag til hele historikken — teksten skal kunne sige begge dele uden at lyve.
  const periodLabel = filterFrom && filterTo ? `${fmtDate(filterFrom)} – ${fmtDate(filterTo)}`
    : filterFrom ? `Fra ${fmtDate(filterFrom)}`
    : filterTo   ? `Til og med ${fmtDate(filterTo)}`
    : 'Hele perioden';

  // PDF som alternativ til CSV: samme udvalg, men til at læse og sende videre frem for at regne
  // i. Derfor de kolonner der står på skærmen — booking-UID og noter hører til i CSV'en, hvor de
  // kan søges i. Liggende A4, fordi syv kolonner med tekst ikke går på en stående side.
  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('GoMore-konto — kontoudtog', 14, 16);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(periodLabel, 14, 22);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Genereret ${new Date().toLocaleDateString('da-DK')}${user?.name ? ' for ' + user.name : ''}`, 14, 27);
    doc.setTextColor(0);

    // Saldoen er kontoens tilstand efter NYESTE postering — ikke en sum af det filtrerede udvalg.
    // Filtrerer man en enkelt måned frem, står saldoen stadig som den er i dag, og det skal
    // fremgå. Ellers ville tallet blive læst som "månedens resultat".
    doc.setFontSize(9);
    doc.text(`${visiblePostings.length} posteringer`, pageWidth - 14, 16, { align: 'right' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Aktuel saldo: ${fmtAmount(aktuelSaldo)} DKK`, pageWidth - 14, 22, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text('Saldo pr. nyeste postering', pageWidth - 14, 27, { align: 'right' });
    doc.setTextColor(0);

    autoTable(doc, {
      startY: 33,
      head: [['Dato', 'Type', 'Lejer / detalje', 'Bil', 'Beløb', 'Saldo', 'Match']],
      body: visiblePostings.map(p => {
        const d = getDisplay(p.category, Number(p.amount));
        const detail = describeBeyondLabel(p.description, d.label);
        return [
          fmtDate(p.posting_date),
          d.label,
          [p.renter_name, detail].filter(Boolean).join(' · '),
          p.registration_number ?? '',
          fmtAmount(Number(p.amount)),
          fmtAmount(Number(p.saldo)),
          MATCH_LABEL[p.match_type] ?? p.match_type,
        ];
      }),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [236, 233, 226], textColor: 50, fontStyle: 'bold' },
      columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' } },
      margin: { bottom: 18 },
      didParseCell: (data) => {
        // Penge ud og ikke-matchede rækker markeres rødt, samme signal som på skærmen.
        if (data.section !== 'body') return;
        const row = visiblePostings[data.row.index];
        if (!row) return;
        if (data.column.index === 4 && Number(row.amount) < 0) data.cell.styles.textColor = [190, 40, 40];
        if (data.column.index === 6 && row.match_type === 'unmatched') data.cell.styles.textColor = [190, 40, 40];
      },
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(`Side ${i} af ${pageCount}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
      doc.setTextColor(0);
    }

    const now = new Date();
    const p2 = (n: number) => String(n).padStart(2, '0');
    doc.save(`gomore-konto-${now.getFullYear()}-${p2(now.getMonth() + 1)}-${p2(now.getDate())}.pdf`);
  };

  const categories: PaymentCategory[] = ['rental','extra','credit','cancellation','refusion','subscription','boost','bank','owed','other'];

  return (
    <main className="px-4 md:px-8 py-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">GoMore-konto</h1>
          <p className="text-sm text-ink-500 mt-0.5">Kontoudtog med løbende saldo · auto-matchet mod udlejninger</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" iconLeft={<Download size={14} />}
            onClick={exportCsv} disabled={visiblePostings.length === 0}
            title="Hent det filtrerede udvalg som CSV — til afstemning i Excel">
            CSV
          </Button>
          <Button variant="ghost" size="sm" iconLeft={<FileText size={14} />}
            onClick={exportPdf} disabled={visiblePostings.length === 0}
            title="Hent det filtrerede udvalg som PDF — til at læse og sende videre">
            PDF
          </Button>
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={14} />} onClick={reload}>
            Opdater
          </Button>
          <Link to="/gomore/import">
            <Button variant="sage" size="sm" iconLeft={<Upload size={14} />}>
              Importér kontoudtog
            </Button>
          </Link>
        </div>
      </div>

      {/* Saldo + kategori-oversigt */}
      <div className="grid md:grid-cols-[auto_1fr] gap-4 mb-6">
        <div className="list-card p-4 min-w-[220px]">
          <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">Aktuel saldo</div>
          <div className={`text-3xl font-bold ${aktuelSaldo < 0 ? 'text-rose-700' : 'text-sage-700'}`}>
            {fmtAmount(aktuelSaldo)} <span className="text-base font-normal text-ink-500">DKK</span>
          </div>
          <div className="text-xs text-ink-500 mt-1">{postings.length} posteringer</div>
        </div>
        {summary && (
          <div className="list-card p-4">
            <div className="text-xs uppercase tracking-wider text-ink-500 mb-2">Pr. kategori (i valgt periode)</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {summary.by_category.map(c => (
                <div key={c.category} className="text-sm">
                  <div className="text-xs text-ink-500">{CATEGORY_LABEL[c.category]}</div>
                  <div className={`font-semibold ${c.total < 0 ? 'text-rose-700' : 'text-ink-900'}`}>
                    {fmtAmount(c.total)}
                  </div>
                  <div className="text-[11px] text-ink-400">{c.count} stk</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Filtre */}
      <div className="list-card p-3 mb-4 flex flex-wrap gap-3 items-center">
        <label className="text-sm flex items-center gap-2">
          Fra:
          <input type="date" className="filter-input" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
        </label>
        <label className="text-sm flex items-center gap-2">
          Til:
          <input type="date" className="filter-input" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
        </label>
        <label className="text-sm flex items-center gap-2">
          Kategori:
          <select className="filter-input" value={filterCategory} onChange={e => setFilterCategory(e.target.value as PaymentCategory | '')}>
            <option value="">Alle</option>
            {categories.map(c => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
          </select>
        </label>
        <label className="text-sm flex items-center gap-2">
          {t('gomore_account.filter_show')}:
          <select className="filter-input" value={show} onChange={e => setShow(e.target.value as typeof show)}
            title={show === 'mismatch' ? t('gomore_account.filter_mismatch_hint') : undefined}>
            <option value="all">{t('gomore_account.filter_all')}</option>
            <option value="unmatched">{t('gomore_account.filter_unmatched')}</option>
            <option value="mismatch">{t('gomore_account.filter_mismatch')}</option>
          </select>
        </label>
        {(filterFrom || filterTo || filterCategory || show !== 'all') && (
          <button type="button" onClick={() => { setFilterFrom(''); setFilterTo(''); setFilterCategory(''); setShow('all'); }}
            className="text-sm text-ink-500 hover:text-ink-900 underline">Ryd</button>
        )}
      </div>

      {/* Tabel */}
      <div className="list-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink-500">Indlæser…</div>
        ) : postings.length === 0 ? (
          <div className="p-8 text-center text-ink-500">
            Ingen posteringer endnu. Brug <Link to="/gomore/import" className="text-sage-700 underline">importér kontoudtog</Link> for at uploade JSON.
          </div>
        ) : visiblePostings.length === 0 ? (
          <div className="p-8 text-center text-ink-500">Ingen posteringer matcher dine filtre.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2">Dato</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Lejer / Detalje</th>
                  <th className="text-left px-3 py-2">Bil</th>
                  <th className="text-right px-3 py-2">Beløb</th>
                  <th className="text-right px-3 py-2">Saldo</th>
                  <th className="text-left px-3 py-2">Match</th>
                </tr>
              </thead>
              <tbody>
                {pagedPostings.map(p => (
                  <tr key={p.id} className="row-hover">
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(p.posting_date)}</td>
                    <td className="px-3 py-2">
                      {(() => {
                        const disp = getDisplay(p.category, p.amount);
                        const detail = describeBeyondLabel(p.description, disp.label);
                        return (
                          <>
                            <span className={`text-xs px-2 py-0.5 rounded ${disp.colorClass}`}>{disp.label}</span>
                            {detail && <div className="text-[11px] text-ink-400 mt-0.5">{detail}</div>}
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2">{p.renter_name || <span className="text-ink-400">—</span>}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{p.registration_number || <span className="text-ink-400">—</span>}</td>
                    <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap font-medium ${p.amount < 0 ? 'text-rose-700' : 'text-ink-900'}`}>
                      {fmtAmount(p.amount)}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${p.saldo < 0 ? 'text-rose-700' : 'text-ink-700'}`}>
                      {fmtAmount(p.saldo)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          {(p.match_type === 'auto' || p.match_type === 'manual') && p.booking_id && p.booking_uid && (
                            <button type="button" onClick={() => openBooking(p.booking_id!)}
                              className="text-xs text-sage-700 hover:underline inline-flex items-center gap-1">
                              UID {p.booking_uid}
                              {p.match_type === 'manual' && <span className="text-ink-400">(manuel)</span>}
                              <ExternalLink size={11} />
                            </button>
                          )}
                          {p.match_type === 'ignored' && <span className="text-xs text-ink-400">Ignoreret</span>}
                          {p.match_type === 'unmatched' && <span className="text-xs text-rose-700">Ikke matched</span>}
                        </div>
                        <button type="button" onClick={() => setEditPosting(p)}
                          title="Ret match"
                          className="w-6 h-6 flex items-center justify-center rounded text-ink-400 hover:bg-cream-100 hover:text-ink-700">
                          <Pencil size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-ink-100 text-sm text-ink-500">
            <span>{t('pagination.showing', {
              from: (shownPage - 1) * pageSize + 1,
              to: Math.min(shownPage * pageSize, visiblePostings.length),
              total: visiblePostings.length,
            })}</span>
            <Pagination page={shownPage} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </div>

      {editPosting && (
        <MatchPostingModal
          posting={editPosting}
          onClose={() => setEditPosting(null)}
          onSaved={() => { setEditPosting(null); reload(); }}
        />
      )}

      {viewBooking && (
        <BookingDetailModal
          invoice={viewBooking}
          onClose={() => setViewBooking(null)}
          onSave={updated => { setViewBooking(updated); reload(); }}
        />
      )}
    </main>
  );
}
